import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt, PenNib, Plus, CheckCircle, Sparkle } from "@phosphor-icons/react";

export default function CampaignModal({ drafts, onClose }) {
  const [items, setItems] = useState(drafts.map((d) => ({ ...d, emailSent: false, loiSent: false, added: false })));
  const [busy, setBusy] = useState("");

  const update = (i, patch) => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const sendEmail = async (i) => {
    const it = items[i];
    if (!it.email.trim()) { toast.error("Email du destinataire manquant"); return; }
    setBusy(`email-${i}`);
    try {
      await api.post("/outreach/email", { company_id: it.company_id, to: it.email.trim(), subject: it.subject, body: it.body });
      await api.post("/deals", { company_id: it.company_id }).catch(() => {});
      update(i, { emailSent: true, added: true });
      toast.success(`Email envoyé à ${it.nom}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec envoi email"); }
    finally { setBusy(""); }
  };

  const sendLoi = async (i) => {
    const it = items[i];
    if (!it.email.trim()) { toast.error("Email du signataire manquant"); return; }
    setBusy(`loi-${i}`);
    try {
      await api.post(`/companies/${it.company_id}/docusign/send`, { kind: "loi", signer_email: it.email.trim(), mode: "email" });
      update(i, { loiSent: true });
      toast.success(`LOI envoyée en signature à ${it.nom}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec envoi LOI"); }
    finally { setBusy(""); }
  };

  const addPipeline = async (i) => {
    const it = items[i];
    setBusy(`add-${i}`);
    try {
      await api.post("/deals", { company_id: it.company_id });
      update(i, { added: true });
      toast.success(`${it.nom} ajouté au pipeline`);
    } catch (e) {
      if (e.response?.status === 400) { update(i, { added: true }); toast.info("Déjà dans le pipeline"); }
      else toast.error("Erreur ajout pipeline");
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="campaign-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[88vh] flex flex-col border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2"><Sparkle size={18} className="text-primary" weight="fill" /><span className="font-head font-bold">Campagne d'approche · {items.length} cible(s)</span></div>
          <button data-testid="campaign-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-5">
          {items.map((it, i) => (
            <div key={it.company_id} className="border border-border p-4" data-testid={`campaign-item-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">{it.nom} <span className="text-xs text-muted-foreground font-normal">· {it.ville}</span></div>
                {it.added && <span className="flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle size={12} weight="fill" /> Pipeline</span>}
              </div>
              <input data-testid={`campaign-email-${i}`} value={it.email} onChange={(e) => update(i, { email: e.target.value })}
                placeholder="Email du destinataire" className="w-full mb-2 px-2.5 py-2 text-sm border border-border outline-none focus:border-primary" />
              <input data-testid={`campaign-subject-${i}`} value={it.subject} onChange={(e) => update(i, { subject: e.target.value })}
                placeholder="Objet" className="w-full mb-2 px-2.5 py-2 text-sm font-medium border border-border outline-none focus:border-primary" />
              <textarea data-testid={`campaign-body-${i}`} value={it.body} onChange={(e) => update(i, { body: e.target.value })}
                rows={5} className="w-full mb-3 px-2.5 py-2 text-sm border border-border outline-none focus:border-primary resize-y" />
              <div className="flex flex-wrap gap-2">
                <button data-testid={`campaign-send-email-${i}`} onClick={() => sendEmail(i)} disabled={busy === `email-${i}` || it.emailSent}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
                  <PaperPlaneTilt size={13} /> {it.emailSent ? "Email envoyé ✓" : busy === `email-${i}` ? "Envoi…" : "Envoyer l'email"}
                </button>
                <button data-testid={`campaign-send-loi-${i}`} onClick={() => sendLoi(i)} disabled={busy === `loi-${i}` || it.loiSent}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50">
                  <PenNib size={13} /> {it.loiSent ? "LOI envoyée ✓" : busy === `loi-${i}` ? "Envoi…" : "Envoyer LOI (DocuSign)"}
                </button>
                {!it.added && (
                  <button data-testid={`campaign-add-${i}`} onClick={() => addPipeline(i)} disabled={busy === `add-${i}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-border hover:bg-accent disabled:opacity-50">
                    <Plus size={13} /> Pipeline
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-border text-[11px] text-muted-foreground shrink-0">
          Les emails sont rédigés par l'IA — relis et ajuste avant d'envoyer. L'envoi LOI crée une enveloppe DocuSign réelle.
        </div>
      </div>
    </div>
  );
}
